import { API_ENDPOINTS } from '@/constants';
import type {
  CombinedTransferTargetsResponse,
  GetAgentCcpStatusForUsersOutputDTO,
  GetAgentCcpStatusForUsersParams,
  RequestTransferInput,
  RequestTransferOutput,
  RespondTransferInput,
  RespondTransferOutput,
} from '@/types/agents';
import type {
  StartWarmTransferInput,
  StartWarmTransferOutput,
  WarmTransferActiveContext,
} from '@/types/warmTransfer';
import type { ApiBuilder } from './types';

export type CancelWarmTransferInput = {
  initialContactId: string;
  reason:
    | 'cancelled_by_agent1'
    | 'declined_by_agent2'
    | 'consult_ended'
    | 'consult_agent_disconnected'
    | 'completed';
};

export type WarmConsultConnectedInput = {
  initialContactId: string;
  answeringConnectId?: string;
  answeringDisplayName?: string;
};

/**
 * Agents API (CCP status for users, etc.).
 * Requires ManagerGuard (manager only).
 */
export const agentsApi = (builder: ApiBuilder) => ({
  getAgentCcpStatusForUsers: builder.query<
    GetAgentCcpStatusForUsersOutputDTO,
    GetAgentCcpStatusForUsersParams
  >({
    query: ({ connectIds }) => {
      const search =
        connectIds.length > 0
          ? `?${connectIds
              .map((id: string) => `connectIds=${encodeURIComponent(id)}`)
              .join('&')}`
          : '';
      return {
        url: `${API_ENDPOINTS.AGENTS.CCP_STATUS_FOR_USERS}${search}`,
      };
    },
    providesTags: (_result, _error, { connectIds }) => [
      { type: 'Agents', id: connectIds.join(',') },
    ],
  }),

  getCombinedTransferTargets: builder.query<
    CombinedTransferTargetsResponse,
    void
  >({
    query: () => ({
      url: `${API_ENDPOINTS.AGENTS.TRANSFER_TARGETS}`,
      method: 'GET',
    }),
    providesTags: ['Agents'],
    keepUnusedDataFor: 15,
  }),

  requestAgentTransfer: builder.mutation<
    RequestTransferOutput,
    RequestTransferInput
  >({
    query: body => ({
      url: API_ENDPOINTS.AGENTS.TRANSFERS_REQUEST,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['Agents'],
  }),

  respondAgentTransfer: builder.mutation<
    RespondTransferOutput,
    RespondTransferInput
  >({
    query: body => ({
      url: API_ENDPOINTS.AGENTS.TRANSFERS_RESPOND,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['Agents'],
  }),

  startWarmTransfer: builder.mutation<
    StartWarmTransferOutput,
    StartWarmTransferInput
  >({
    query: body => ({
      url: API_ENDPOINTS.AGENTS.TRANSFERS_WARM_START,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['Agents'],
  }),

  getActiveTransferContext: builder.query<
    WarmTransferActiveContext,
    { initialContactId: string }
  >({
    query: ({ initialContactId }) => ({
      url: `${API_ENDPOINTS.AGENTS.TRANSFERS_ACTIVE_CONTEXT}?initialContactId=${encodeURIComponent(initialContactId)}`,
      method: 'GET',
    }),
  }),

  cancelWarmTransfer: builder.mutation<void, CancelWarmTransferInput>({
    query: body => ({
      url: API_ENDPOINTS.AGENTS.TRANSFERS_WARM_CANCEL,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['Agents'],
  }),

  markWarmConsultConnected: builder.mutation<void, WarmConsultConnectedInput>({
    query: body => ({
      url: API_ENDPOINTS.AGENTS.TRANSFERS_WARM_CONSULT_CONNECTED,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['Agents'],
  }),

  completeWarmTransferSession: builder.mutation<
    void,
    {
      initialContactId: string;
      promoteConsultToPrimary?: boolean;
      reason?: 'completed_by_agent1';
    }
  >({
    query: body => ({
      url: API_ENDPOINTS.AGENTS.TRANSFERS_WARM_COMPLETE,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['Agents'],
  }),
});
